// Error tracking interno (D43 directiva CEO 2026-05-07).
//
// Reemplaza Sentry SaaS externo. Captura errores globales + Vue + promesas
// no manejadas y los envía al backend `/api/v1/events/report` (tabla
// app_events). El CEO los ve en /admin/eventos con filtros y workflow.
//
// Si el envío falla (offline, backend caído), se persiste en localStorage
// para reintento al siguiente paint.
//
// API estable `trackError(err, ctx)` — el resto del código no necesita
// importar nada distinto si en el futuro se migra a otro tracker.

interface ErrorContext {
  source?: 'window' | 'unhandled_promise' | 'vue' | 'manual';
  url?: string;
  userId?: string;
  clinicId?: string;
  [key: string]: unknown;
}

interface CapturedError {
  message: string;
  stack?: string;
  ts: number;
  context: ErrorContext;
  userAgent: string;
}

const LOCAL_STORAGE_KEY = 'wiservet:client-errors';
const MAX_LOCAL_ERRORS = 50;

function persistLocally(captured: CapturedError) {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY) || '[]';
    const list: CapturedError[] = JSON.parse(raw);
    list.push(captured);
    if (list.length > MAX_LOCAL_ERRORS) list.splice(0, list.length - MAX_LOCAL_ERRORS);
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(list));
  } catch {
    // localStorage full o disabled — silencio.
  }
}

export function trackError(err: unknown, context: ErrorContext = {}): void {
  const message = err instanceof Error ? err.message : String(err);
  const stack = err instanceof Error ? err.stack : undefined;
  const captured: CapturedError = {
    message,
    stack,
    ts: Date.now(),
    context: {
      source: 'manual',
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      ...context,
    },
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'ssr',
  };

  // Emit a console.error siempre — devs lo ven en DevTools.
  // eslint-disable-next-line no-console
  console.error('[error-tracking]', captured);

  // Persist en localStorage como fallback si el envío falla.
  persistLocally(captured);

  // Despachar al backend /api/v1/events/report (tabla app_events).
  // El handler dedupea via fingerprint (sha256 de title+stack), así que un
  // mismo error reportado N veces incrementa occurrence_count en lugar de
  // crear duplicados. Auth opcional — si hay JWT en sessionStorage, lo
  // mandamos; sino el evento queda anónimo con user_agent + url únicamente.
  void reportToBackend(captured);
}

const REPORT_ENDPOINT = '/api/v1/events/report';

async function reportToBackend(captured: CapturedError): Promise<void> {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const token = typeof sessionStorage !== 'undefined'
      ? sessionStorage.getItem('wiservet:access_token')
      : null;
    if (token) headers['Authorization'] = 'Bearer ' + token;

    // Description = mensaje técnico crudo (queda como referencia). Title =
    // versión humana parseada para que el dashboard sea legible. El backend
    // dedupea por hash(title+stack) — si los mensajes técnicos son distintos,
    // siguen creando filas separadas.
    const technical = captured.message || '(sin mensaje)';
    const body = {
      title: humanizeTitle(captured) || technical.slice(0, 280),
      description: technical.slice(0, 1500),
      error_stack: captured.stack ?? '',
      error_url: captured.context.url ?? '',
      severity: captured.context.severity as string ?? 'medium',
    };

    await fetch(REPORT_ENDPOINT, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      keepalive: true,
    });
  } catch {
    // Network error — fallback a localStorage ya hecho.
  }
}

// humanizeTitle convierte un error JS técnico en un título legible para
// CEO no técnico. Ejemplos:
//   "Failed to fetch dynamically imported module: .../FinanzasView-abc.js"
//     → "No cargó la sección Finanzas (probable cache vieja)"
//   "Cannot read properties of null (reading 'length')"
//     → "Algo se rompió al renderizar (datos faltantes)"
//   "NetworkError when attempting to fetch resource"
//     → "Falló una conexión de red"
function humanizeTitle(captured: CapturedError): string | null {
  const msg = captured.message || '';
  const url = captured.context.url ?? '';
  const route = (() => {
    try {
      return url ? new URL(url).pathname : '';
    } catch {
      return '';
    }
  })();

  if (msg.includes('Failed to fetch dynamically imported module')) {
    const m = msg.match(/\/assets\/([A-Za-z0-9_-]+?)(?:-[A-Za-z0-9_-]+)?\.js/);
    const view = m ? m[1].replace(/View$/, '') : '';
    return view
      ? `No cargó la sección "${view}" (probable cache vieja del navegador)`
      : 'No cargó una sección de la app (probable cache vieja del navegador)';
  }
  if (msg.includes('Cannot read propert')) {
    return route
      ? `Algo se rompió al renderizar la página ${route} (datos faltantes)`
      : 'Algo se rompió al renderizar (datos faltantes)';
  }
  if (msg.toLowerCase().includes('network') && msg.toLowerCase().includes('fetch')) {
    return 'Falló una conexión de red';
  }
  if (msg.includes('TypeError')) {
    return route
      ? `Error de tipo en la página ${route}`
      : 'Error de tipo en JavaScript';
  }
  if (msg.includes('ReferenceError')) {
    return 'Variable no definida en JavaScript';
  }
  return null; // Caller cae al raw message recortado.
}

export function getCapturedErrors(): CapturedError[] {
  try {
    return JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
}

export function clearCapturedErrors(): void {
  try {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
  } catch {
    // noop
  }
}

// Instala handlers globales del browser. Llamar una vez en main.ts.
export function installGlobalErrorHandlers(): void {
  if (typeof window === 'undefined') return;

  window.addEventListener('error', (event: ErrorEvent) => {
    // Cross-origin scripts (ej. js.fintoc.com) reportan "Script error." sin filename
    // porque el browser aplica same-origin policy — no son accionables.
    if (event.message === 'Script error.' && !event.filename) {
      event.preventDefault();
      return;
    }
    // Bug del SDK de Fintoc: intenta clonar callbacks Vue via postMessage al iframe.
    // Las funciones no son clonables con Structured Clone. No afecta el happy path.
    if (event.error instanceof DOMException && event.error.name === 'DataCloneError') {
      event.preventDefault();
      return;
    }
    trackError(event.error ?? event.message, {
      source: 'window',
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    trackError(event.reason, { source: 'unhandled_promise' });
  });
}
